import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, from, map, Observable } from 'rxjs';
import { API_BASE_URL } from '@/app/core/http/api-base-url.token';
import { AuthHttpClient } from '@/app/core/http/auth-http-client';
import { rethrowWithParsedBody } from '@/app/core/http/blob-error-response.util';
import { filenameFromContentDisposition } from '@/app/shared/utils/content-disposition.util';
import {
  HospitalAccountDownloadFile,
  HospitalAccountDownloadFormat,
  HospitalAccountDownloadRequest,
} from '../models/hospital-account-download.model';
import { HospitalAccountDownloadRepository } from './hospital-account-download.repository';
import { toApiFilterParams } from './hospital-account.mapper';

const DOWNLOAD_PATH = '/cuentas-hospitalarias/descarga';

@Injectable()
export class HospitalAccountDownloadHttpRepository extends HospitalAccountDownloadRepository {
  private readonly http = inject(AuthHttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  override download(
    request: HospitalAccountDownloadRequest,
  ): Observable<HospitalAccountDownloadFile> {
    const { format, ...filters } = request;
    return this.http
      .getBlob(`${this.baseUrl}${DOWNLOAD_PATH}`, {
        params: { ...toApiFilterParams(filters), formato: format },
      })
      .pipe(
        map((response) => toDownloadFile(response, format)),
        catchError((cause: unknown) => from(rethrowWithParsedBody(cause))),
      );
  }
}

function toDownloadFile(
  response: HttpResponse<Blob>,
  format: HospitalAccountDownloadFormat,
): HospitalAccountDownloadFile {
  if (!response.body) throw new Error('Hospital accounts download response has no body.');
  return {
    blob: response.body,
    fileName:
      filenameFromContentDisposition(response.headers.get('Content-Disposition')) ??
      defaultFileName(format),
  };
}

/** Mismo patrón que arma el back, por si el header no llega. */
export function defaultFileName(format: HospitalAccountDownloadFormat, today = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  const date = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  return `Cuentas_Hospitalarias_${date}.${format}`;
}
